import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/store/store";
import {
  setCurrentPeriod,
  setSelectedReportId,
  setCurrentView,
  setExpandedAccordions,
  setSavedScrollPosition,
  setCurrentTaskId,
} from "@/store/analysisSlice";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Loader2, AlertCircle, Sparkles, TrendingUp, ExternalLink, Calendar, BarChart3, FileText, X, PanelLeftClose, Lightbulb } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useEvent } from "@/hooks/useEvent";
import { useAssetById } from "@/hooks/useAssetById";
import { EventImageLightbox } from "@/components/assets/EventImageLightbox";
import type { DiffReport } from "@/types/analysis";
import type { ClaudeTask } from "@/types/claudeTask";
import { format, formatDistanceToNow, parseISO } from "date-fns";

interface DiffAnalysisSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: any;
}

interface TrendExample {
  id: string;
  hook: string;
  asset_id: string | null;
  relevanceScore: number;
  created_at: string;
}

interface Trend {
  name: string;
  description: string;
  summary: string;
  examples: TrendExample[];
}

interface Insight {
  title: string;
  description: string;
}

interface TrendsReport {
  period: string;
  trends: Trend[];
  metadata: {
    analysisDate: string;
    eventCount: number;
    companiesCount: number;
    trendsCount: number;
  };
}

// Helper function to get ISO week number
function getISOWeek(date: Date): number {
  const tempDate = new Date(date.valueOf());
  const dayNum = (date.getDay() + 6) % 7;
  tempDate.setDate(tempDate.getDate() - dayNum + 3);
  const firstThursday = tempDate.valueOf();
  tempDate.setMonth(0, 1);
  if (tempDate.getDay() !== 4) {
    tempDate.setMonth(0, 1 + ((4 - tempDate.getDay()) + 7) % 7);
  }
  return 1 + Math.ceil((firstThursday - tempDate.valueOf()) / 604800000);
}

// Helper function to get current week in YYYYWN format
function getCurrentWeek(): string {
  const now = new Date();
  const year = now.getFullYear();
  const week = getISOWeek(now);
  return `${year}W${week}`;
}

// Helper function to convert quarter format "2025 Q4" to "2025Q4"
function normalizeQuarterFormat(quarter: string | undefined): string | undefined {
  if (!quarter || quarter === 'all') return undefined;

  // Handle "2025 Q4" format
  const match = quarter.match(/(\d{4})\s*Q(\d)/);
  if (match) {
    return `${match[1]}Q${match[2]}`;
  }

  // Already in correct format or other format
  return quarter;
}

// Helper function to determine period from filters
function getPeriodFromFilters(filters: any): string | undefined {
  // First, check if periods array is set (from Diffs page filter)
  if (filters?.periods && Array.isArray(filters.periods) && filters.periods.length > 0) {
    // Use the first period from the array
    return filters.periods[0];
  }

  // If time_period is 'week', use current week
  if (filters?.time_period === 'week') {
    return getCurrentWeek();
  }

  // If time_period is 'quarter' and quarter is set, use that quarter
  if (filters?.time_period === 'quarter' && filters?.quarter) {
    return normalizeQuarterFormat(filters.quarter);
  }

  // Fallback to just quarter if set (for backwards compatibility)
  if (filters?.quarter) {
    return normalizeQuarterFormat(filters.quarter);
  }

  return undefined;
}

// Helper function to format period for display
function formatPeriodDisplay(period: string): string {
  // Convert "2025Q4" to "2025 Q4"
  const quarterMatch = period.match(/(\d{4})Q(\d)/);
  if (quarterMatch) {
    return `${quarterMatch[1]} Q${quarterMatch[2]}`;
  }

  // Convert "2025W47" to "2025 Week 47"
  const weekMatch = period.match(/(\d{4})W(\d+)/);
  if (weekMatch) {
    return `${weekMatch[1]} Week ${weekMatch[2]}`;
  }

  return period;
}

export function DiffAnalysisSheet({
  open,
  onOpenChange,
  filters,
}: DiffAnalysisSheetProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const selectedReportId = useSelector((state: RootState) => state.analysis.selectedReportId);
  const currentView = useSelector((state: RootState) => state.analysis.currentView);
  const expandedAccordions = useSelector((state: RootState) => state.analysis.expandedAccordions);
  const savedScrollPosition = useSelector((state: RootState) => state.analysis.savedScrollPosition);
  const currentTaskId = useSelector((state: RootState) => state.analysis.currentTaskId);
  const storedPeriod = useSelector((state: RootState) => state.analysis.currentPeriod);

  // Local state (not persisted)
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [reportContent, setReportContent] = useState<TrendsReport | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [lightboxState, setLightboxState] = useState<{
    isOpen: boolean;
    eventId: string | null;
    currentImage: 'before' | 'after';
  }>({
    isOpen: false,
    eventId: null,
    currentImage: 'after'
  });
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Get the period from filters
  const period = getPeriodFromFilters(filters);

  // Track period changes and update Redux (fixes stale task bug)
  useEffect(() => {
    if (period !== storedPeriod) {
      dispatch(setCurrentPeriod(period || null));
    }
  }, [period, storedPeriod, dispatch]);

  // Handler to prevent sheet from closing when lightbox is open
  const handleSheetOpenChange = (newOpen: boolean) => {
    // If lightbox is open and user tries to close sheet, intercept and close lightbox instead
    if (!newOpen && lightboxState.isOpen) {
      setLightboxState({
        isOpen: false,
        eventId: null,
        currentImage: 'after'
      });
      return; // Don't close the sheet
    }
    // Otherwise, allow normal sheet open/close behavior
    onOpenChange(newOpen);
  };

  // Save scroll position when lightbox opens
  useEffect(() => {
    if (lightboxState.isOpen) {
      const scrollArea = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollArea) {
        dispatch(setSavedScrollPosition(scrollArea.scrollTop));
      }
    }
  }, [lightboxState.isOpen, dispatch]);

  // Restore scroll position when lightbox closes
  useEffect(() => {
    if (!lightboxState.isOpen && savedScrollPosition > 0) {
      const scrollArea = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollArea) {
        scrollArea.scrollTop = savedScrollPosition;
      }
    }
  }, [lightboxState.isOpen, savedScrollPosition]);

  // Query for all reports matching the period
  const { data: allReports, refetch: refetchReports } = useQuery({
    queryKey: ['diffReports', period],
    queryFn: async (): Promise<DiffReport[]> => {
      if (!period) return [];

      const { data, error } = await supabase
        .from('app_diff_reports')
        .select('*')
        .eq('period', period)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[DiffAnalysisSheet] Error fetching reports:', error);
        return [];
      }

      return data as DiffReport[];
    },
    enabled: !!period && open,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: (data) => {
      // Keep refetching if any report doesn't have json_url yet (still generating)
      if (data && data.some(report => !report.json_url)) {
        return 5000; // 5 seconds
      }
      return false;
    },
  });

  // Get the selected report or the latest one
  const selectedReport = selectedReportId
    ? allReports?.find(r => r.id === selectedReportId)
    : allReports?.[0];

  // Query for task status if we have a task ID
  const { data: currentTask } = useQuery({
    queryKey: ['claudeTask', currentTaskId],
    queryFn: async (): Promise<ClaudeTask | null> => {
      if (!currentTaskId) return null;

      const { data, error } = await supabase
        .from('claude_tasks')
        .select('*')
        .eq('id', currentTaskId)
        .single();

      if (error) {
        console.error('Error fetching task:', error);
        return null;
      }

      return data as ClaudeTask;
    },
    enabled: !!currentTaskId && open,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: (data) => {
      // Keep refetching while task is pending or running
      if (data?.status === 'pending' || data?.status === 'running') {
        return 3000; // 3 seconds
      }
      return false;
    },
  });

  // Load report content when report has json_url
  useEffect(() => {
    const loadReportContent = async () => {
      if (selectedReport?.json_url) {
        setLoadingContent(true);
        try {
          // Check if json_url is already a full URL
          const fetchUrl = selectedReport.json_url.startsWith('http')
            ? selectedReport.json_url
            : supabase.storage
                .from('reports')
                .getPublicUrl(selectedReport.json_url.replace('reports/', '')).data.publicUrl;

          // Fetch the JSON content
          const response = await fetch(fetchUrl);
          if (response.ok) {
            const content = await response.json();
            setReportContent(content);
          } else {
            console.error('Failed to fetch report content:', response.status, response.statusText);
            setReportContent(null);
            toast({
              title: 'Failed to load report',
              description: 'Could not fetch report content.',
              variant: 'destructive',
            });
          }
        } catch (error) {
          console.error('Error loading report content:', error);
          setReportContent(null);
          toast({
            title: 'Error loading report',
            description: 'An error occurred while loading the report.',
            variant: 'destructive',
          });
        } finally {
          setLoadingContent(false);
        }
      } else {
        setReportContent(null);
      }
    };

    loadReportContent();
  }, [selectedReport, toast]);

  // Refetch reports when task completes
  useEffect(() => {
    if (currentTask?.status === 'completed') {
      refetchReports();
      dispatch(setCurrentTaskId(null));
      // Switch to details view and show success
      dispatch(setCurrentView('details'));
      toast({
        title: 'Report generated',
        description: 'Your analysis report is ready.',
      });
    } else if (currentTask?.status === 'failed') {
      toast({
        title: 'Task failed',
        description: 'The analysis task failed. Please try again.',
        variant: 'destructive',
      });
      dispatch(setCurrentTaskId(null));
    }
  }, [currentTask?.status, refetchReports, toast, dispatch]);

  const createAnalysisTask = async () => {
    if (!period) {
      toast({
        title: 'No period selected',
        description: 'Please select a quarter or time period to analyze.',
        variant: 'destructive',
      });
      return;
    }

    setIsCreatingTask(true);

    try {
      // Create the task
      const { data: task, error } = await supabase
        .from('claude_tasks')
        .insert({
          command: 'analyze',
          slug: filters?.slug || 'all',
          params: {
            period,
            slugs: filters?.slug ? [filters.slug] : undefined,
            customPrompt: customPrompt || undefined,
          },
          status: 'pending',
          priority: 1, // User explicitly requested priority 1
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      dispatch(setCurrentTaskId(task.id));

      toast({
        title: 'Analysis task created',
        description: `Task ${task.id.substring(0, 8)}... has been queued for ${formatPeriodDisplay(period)}.`,
      });
    } catch (error) {
      console.error('Error creating analysis task:', error);
      toast({
        title: 'Error creating task',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingTask(false);
    }
  };

  const renderReportsList = () => {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Available Reports</h2>
          <span className="text-sm text-gray-500">
            {allReports?.length || 0} {period ? `for ${formatPeriodDisplay(period)}` : 'total'}
          </span>
        </div>

        {!allReports || allReports.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-base text-gray-500 mb-6">
              No reports found {period ? `for ${formatPeriodDisplay(period)}` : ''}
            </p>
            <Button
              onClick={() => dispatch(setCurrentView('generate'))}
              size="lg"
            >
              <Sparkles className="h-5 w-5 mr-2" />
              Generate First Report
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {allReports.map((report) => (
              <Card
                key={report.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  selectedReportId === report.id || (!selectedReportId && report.id === allReports[0].id)
                    ? 'ring-2 ring-blue-500 shadow-md'
                    : ''
                }`}
                onClick={() => {
                  dispatch(setSelectedReportId(report.id));
                  dispatch(setCurrentView('details'));
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {!report.json_url && (
                          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                        )}
                        <h3 className="text-lg font-semibold text-gray-900">
                          {formatPeriodDisplay(report.period)}
                        </h3>
                      </div>
                      {report.created_at && (
                        <p className="text-sm text-gray-500 mb-3">
                          Generated {formatDistanceToNow(new Date(report.created_at), { addSuffix: true })}
                        </p>
                      )}
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        {report.companies_count !== undefined && (
                          <span className="flex items-center gap-1">
                            <BarChart3 className="h-4 w-4" />
                            {report.companies_count} companies
                          </span>
                        )}
                        {report.diffs_count !== undefined && (
                          <span>{report.diffs_count} diffs</span>
                        )}
                        {report.trends_count !== undefined && (
                          <span>{report.trends_count} trends</span>
                        )}
                      </div>
                    </div>
                    <ExternalLink className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderGenerateForm = () => {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Generate New Analysis Report</h2>
          {period && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="h-4 w-4" />
              <span>Period: <strong>{formatPeriodDisplay(period)}</strong></span>
            </div>
          )}
        </div>

        {currentTask && (currentTask.status === 'pending' || currentTask.status === 'running') ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-8">
            <div className="flex items-start gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <p className="text-lg text-blue-800 font-semibold mb-2">
                  {currentTask.status === 'pending' ? 'Task Queued' : 'Analysis in Progress'}
                </p>
                <p className="text-sm text-blue-600 mb-4">
                  This may take a few minutes depending on the amount of data to analyze.
                </p>
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <span>Task ID: {currentTask.id.substring(0, 8)}...</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <Card>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-3">
                <Label htmlFor="custom-prompt-main" className="text-base font-medium">
                  Custom Instructions (Optional)
                </Label>
                <Textarea
                  id="custom-prompt-main"
                  placeholder="Add specific instructions for the analysis (e.g., 'Focus on enterprise pricing changes' or 'Highlight security feature updates')..."
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  rows={6}
                  className="resize-none text-base"
                />
                <p className="text-sm text-gray-500">
                  Provide any specific areas of focus or questions you want the analysis to address.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => dispatch(setCurrentView('details'))}
                  disabled={isCreatingTask}
                  size="lg"
                >
                  Cancel
                </Button>
                <Button
                  onClick={createAnalysisTask}
                  disabled={isCreatingTask || !period}
                  size="lg"
                >
                  {isCreatingTask ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Creating Task...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-5 w-5 mr-2" />
                      Generate Report
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderReportDetails = () => {
    if (!selectedReport) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center py-12">
          <FileText className="h-12 w-12 text-gray-300 mb-4" />
          <p className="text-lg font-medium text-gray-900 mb-2">No Report Selected</p>
          <p className="text-sm text-gray-500">
            Select a report from the list to view details
          </p>
        </div>
      );
    }

    if (!selectedReport.json_url) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center py-12">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
          <p className="text-lg font-medium text-gray-900 mb-2">Generating Report...</p>
          <p className="text-sm text-gray-500">
            This page will update automatically when the report is ready
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Report Stats */}
        <div className="grid grid-cols-3 gap-4">
          {selectedReport.companies_count !== undefined && (
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-gray-900 mb-1">{selectedReport.companies_count}</div>
                <div className="text-sm text-gray-600">Companies</div>
              </CardContent>
            </Card>
          )}
          {selectedReport.diffs_count !== undefined && (
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-gray-900 mb-1">{selectedReport.diffs_count}</div>
                <div className="text-sm text-gray-600">Diffs</div>
              </CardContent>
            </Card>
          )}
          {selectedReport.trends_count !== undefined && (
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-gray-900 mb-1">{selectedReport.trends_count}</div>
                <div className="text-sm text-gray-600">Trends</div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Key Insights Section */}
        {selectedReport.analysis_metadata?.insights && selectedReport.analysis_metadata.insights.length > 0 && (
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-blue-600" />
                Key Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {selectedReport.analysis_metadata.insights.map((insight: Insight, idx: number) => (
                  <div key={idx} className="bg-white rounded-lg p-4 border border-blue-100">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">{insight.title}</h4>
                    <p className="text-sm text-gray-700 leading-relaxed">{insight.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Report Content */}
        {loadingContent ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : reportContent ? (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900">Pricing Trends</h2>
            <Accordion
              type="multiple"
              className="w-full space-y-2"
              value={expandedAccordions}
              onValueChange={(value) => dispatch(setExpandedAccordions(value))}
            >
              {reportContent.trends.map((trend, trendIndex) => (
                <AccordionItem key={trendIndex} value={`trend-${trendIndex}`} className="border rounded-lg">
                  <AccordionTrigger className="text-left hover:no-underline px-4 py-4">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 text-base mb-1">
                        {trend.name}
                      </div>
                      <p className="text-sm text-gray-600 font-normal">
                        {trend.summary}
                      </p>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4">
                      <p className="text-sm text-gray-700">{trend.description}</p>
                      <div className="space-y-2">
                        {trend.examples.map((example, exampleIndex) => (
                          <div
                            key={example.id}
                            className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer border border-gray-200"
                            onClick={() => setLightboxState({
                              isOpen: true,
                              eventId: example.id,
                              currentImage: 'after'
                            })}
                          >
                            <div className="flex-shrink-0 mt-0.5">
                              <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-semibold">
                                {exampleIndex + 1}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 line-clamp-2 mb-1">
                                {example.hook}
                              </p>
                              <div className="flex items-center gap-3">
                                {example.relevanceScore !== undefined && (
                                  <span className="text-xs text-gray-500">
                                    Score: {example.relevanceScore.toFixed(1)}
                                  </span>
                                )}
                                {example.created_at && (
                                  <span className="text-xs text-gray-400">
                                    {format(new Date(example.created_at), 'MMM d, yyyy')}
                                  </span>
                                )}
                              </div>
                            </div>
                            <ExternalLink className="flex-shrink-0 h-4 w-4 text-gray-400" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ) : null}
      </div>
    );
  };

  // No period selected warning
  if (!period && open) {
    return (
      <Sheet open={open} onOpenChange={handleSheetOpenChange} modal={false}>
        <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col">
          <SheetHeader className="px-6 py-4 border-b">
            <SheetTitle className="text-lg font-semibold flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-blue-600" />
              Pricing Analysis
            </SheetTitle>
          </SheetHeader>
          <div className="px-6 py-8">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Please select a time period from the filters:
                <ul className="list-disc list-inside mt-2 text-xs">
                  <li>Set "Period" filter to a specific week (e.g., 2025W47)</li>
                </ul>
              </AlertDescription>
            </Alert>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleSheetOpenChange} modal={false}>
        <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col">
          <SheetHeader className="px-6 py-3 border-b">
            <div className="flex items-center justify-between w-full">
              {/* Left side - Split panel toggle (only in details view) */}
              <div className="flex-shrink-0">
                {currentView === 'details' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => dispatch(setCurrentView('list'))}
                    className="flex items-center gap-2"
                    title="View Reports"
                  >
                    <PanelLeftClose className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Right side - New Analysis button */}
              <div className="flex-shrink-0">
                {currentView !== 'generate' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => dispatch(setCurrentView('generate'))}
                    className="flex items-center gap-2"
                  >
                    <Sparkles className="h-4 w-4" />
                    New Analysis
                  </Button>
                )}
              </div>
            </div>
          </SheetHeader>

          {/* Title section below header */}
          <div className="px-6 py-4 border-b bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              {currentView === 'list' ? 'Available Reports' : currentView === 'generate' ? 'New Analysis' : 'Report Details'}
            </h2>
            {period && currentView !== 'list' && (
              <p className="text-sm text-gray-600">
                {formatPeriodDisplay(period)}
              </p>
            )}
          </div>

          <ScrollArea className="flex-1" ref={scrollAreaRef}>
            <div className="px-6 py-6">
              {currentView === 'list' && renderReportsList()}
              {currentView === 'details' && renderReportDetails()}
              {currentView === 'generate' && renderGenerateForm()}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Event Image Lightbox */}
      {lightboxState.isOpen && lightboxState.eventId && (
        <LightboxWithEvent
          eventId={lightboxState.eventId}
          currentImage={lightboxState.currentImage}
          imageType="cropped"
          onClose={() => {
            // Only close lightbox, don't close the sheet
            setLightboxState({
              isOpen: false,
              eventId: null,
              currentImage: 'after'
            });
          }}
          onNavigate={(image) => setLightboxState(prev => ({
            ...prev,
            currentImage: image
          }))}
        />
      )}
    </>
  );
}

// Helper component to fetch event and render lightbox
const LightboxWithEvent: React.FC<{
  eventId: string;
  currentImage: 'before' | 'after';
  imageType: 'markup' | 'cropped' | 'original';
  onClose: () => void;
  onNavigate: (image: 'before' | 'after') => void;
}> = ({ eventId, currentImage, imageType, onClose, onNavigate }) => {
  const { data: event, isLoading } = useEvent(eventId);

  if (isLoading || !event || !event.asset_id) {
    return null;
  }

  return (
    <LightboxWithAsset
      assetId={event.asset_id}
      currentImage={currentImage}
      imageType={imageType}
      description={event.description}
      onClose={onClose}
      onNavigate={onNavigate}
    />
  );
};

// Helper component to fetch asset and render lightbox
const LightboxWithAsset: React.FC<{
  assetId: string;
  currentImage: 'before' | 'after';
  imageType: 'markup' | 'cropped' | 'original';
  description?: string | null;
  onClose: () => void;
  onNavigate: (image: 'before' | 'after') => void;
}> = ({ assetId, currentImage, imageType, description, onClose, onNavigate }) => {
  const { asset, loading } = useAssetById(assetId);

  if (loading || !asset) {
    return null;
  }

  // Helper to get URLs based on imageType
  const getImageUrl = (type: 'primary' | 'secondary') => {
    const isSecondary = type === 'secondary';
    switch (imageType) {
      case 'markup':
        return isSecondary
          ? (asset.secondary_markup_url || asset.secondary_cropped_url || asset.secondary_original_url || asset.secondary_image)
          : (asset.primary_markup_url || asset.primary_cropped_url || asset.primary_original_url || asset.primary_image);
      case 'cropped':
        return isSecondary
          ? (asset.secondary_cropped_url || asset.secondary_original_url || asset.secondary_image)
          : (asset.primary_cropped_url || asset.primary_original_url || asset.primary_image);
      case 'original':
        return isSecondary
          ? (asset.secondary_original_url || asset.secondary_image)
          : (asset.primary_original_url || asset.primary_image);
      default:
        return isSecondary
          ? (asset.secondary_cropped_url || asset.secondary_original_url || asset.secondary_image)
          : (asset.primary_cropped_url || asset.primary_original_url || asset.primary_image);
    }
  };

  // Determine chronological order
  const primaryDate = asset.version ? parseInt(asset.version) : 0;
  const secondaryDate = asset.secondary_version ? parseInt(asset.secondary_version) : 0;
  const shouldSwapForChronology = primaryDate > secondaryDate && getImageUrl('secondary');

  const beforeImageUrl = shouldSwapForChronology ? getImageUrl('secondary') : getImageUrl('primary');
  const afterImageUrl = shouldSwapForChronology ? getImageUrl('primary') : getImageUrl('secondary');
  const beforeVersion = shouldSwapForChronology ? asset.secondary_version : asset.version;
  const afterVersion = shouldSwapForChronology ? asset.version : asset.secondary_version;

  return (
    <EventImageLightbox
      isOpen={true}
      beforeImageUrl={beforeImageUrl}
      afterImageUrl={afterImageUrl}
      currentImage={currentImage}
      beforeVersion={beforeVersion}
      afterVersion={afterVersion}
      assetTitle={asset.title || asset.slug}
      imageType={imageType}
      description={description}
      modal={false}
      onClose={onClose}
      onNavigate={onNavigate}
    />
  );
}
